import React from 'react';
import TechIcon from './TechIcon';
import { BRAND_NAME } from '../constants/brand';

const IMAGE_FRAME =
  'relative w-full h-[260px] sm:h-[300px] lg:h-[340px] rounded-xl overflow-hidden border border-primary-200 shadow-corporate-xl bg-primary-50';

const BusinessProcess = () => {
  const processSteps = [
    {
      step: 1,
      title: 'Entendemos tu Negocio',
      description:
        'Entendemos profundamente tu empresa, procesos actuales y oportunidades de mejora para diseñar la solución que se adapte a tus necesidades.',
      image: '/images/procesos/analisis-procesos-4.jpeg',
      details: [
        'Análisis de procesos actuales',
        'Identificación de cuellos de botella',
        'Evaluación de tecnologías existentes',
        'Mapeo de flujos de trabajo',
      ],
      iconName: 'analysis',
    },
    {
      step: 2,
      title: 'Estandarizamos Procesos',
      description:
        'Optimizamos y estandarizamos tus procesos mediante análisis detallado y mejores prácticas tecnológicas para máxima eficiencia operativa.',
      image: '/images/estandarizacion/estandarizacion-1.jpeg',
      details: [
        'Diseño de flujos optimizados',
        'Documentación de procedimientos',
        'Implementación de mejores prácticas',
        'Capacitación del personal',
      ],
      iconName: 'standardization',
    },
    {
      step: 3,
      title: 'Automatizamos con ERP y Sistemas',
      description:
        'Automatizamos la gestión integrando la plataforma que mejor se adapte a tu negocio: ERP a medida u otros sistemas (CRM, inventarios, producción, etc.) según tus procesos y objetivos.',
      image: '/images/ERP/erp-modulo-compras.jpeg',
      details: [
        'ERP personalizado u otras plataformas según el negocio',
        'Automatización de procesos',
        'Integración de sistemas existentes',
        'Monitoreo y mejora continua',
      ],
      iconName: 'automation',
    },
  ];

  return (
    <section className="py-16 sm:py-20 bg-white relative overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-readable-2xl sm:text-readable-3xl md:text-4xl font-extrabold text-darkBlue-900 font-display tracking-tight mb-4 sm:mb-6">
            Nuestro Proceso de Transformación
          </h2>
          <p className="text-readable-lg sm:text-readable-xl text-secondary-600 max-w-3xl mx-auto font-body leading-relaxed px-4">
            Metodología de {BRAND_NAME}: de la operación al software empresarial que escala con tu negocio
          </p>
          <div className="w-24 h-1 bg-primary-500 mx-auto rounded-full mt-6 shadow-lg" />
        </div>

        <div className="space-y-14 sm:space-y-20">
          {processSteps.map((step, index) => (
            <div
              key={step.step}
              className={`flex flex-col lg:flex-row items-stretch gap-6 sm:gap-8 lg:gap-10 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex-1 bg-white rounded-xl p-6 sm:p-8 shadow-corporate-lg border border-primary-200 flex flex-col h-full">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary-500 flex items-center justify-center text-white shadow-lg mb-4 sm:mb-0 sm:mr-4 shrink-0">
                      <TechIcon name={step.iconName} className="w-9 h-9 sm:w-11 sm:h-11" />
                    </div>
                    <div>
                      <span className="text-readable-base font-bold text-primary-500 uppercase tracking-wider">
                        Paso {step.step}
                      </span>
                      <h3 className="text-readable-xl sm:text-readable-2xl font-bold text-darkBlue-900 font-display">
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-secondary-600 text-readable-base sm:text-readable-lg mb-4 sm:mb-6 font-body leading-relaxed">
                    {step.description}
                  </p>

                  <div className="space-y-2 sm:space-y-3 mt-auto">
                    {step.details.map((detail) => (
                      <div key={detail} className="flex items-start gap-3">
                        <TechIcon name="check" className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                        <span className="text-secondary-600 font-medium text-readable-base">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 flex items-stretch">
                <div className={`${IMAGE_FRAME} flex-1`}>
                  <img
                    src={step.image}
                    alt={step.title}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold text-readable-lg shadow-lg ring-2 ring-white/30">
                    {step.step}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-14 sm:mt-16">
          <div className="bg-primary-50 rounded-lg p-6 sm:p-8 shadow-corporate-lg border border-primary-200 max-w-4xl mx-auto">
            <h3 className="text-readable-xl sm:text-readable-2xl font-bold text-darkBlue-900 mb-3 sm:mb-4 font-display">
              ¿Listo para Transformar tu Empresa?
            </h3>
            <p className="text-secondary-600 mb-4 sm:mb-6 font-body text-readable-base sm:text-readable-lg leading-relaxed">
              Comienza tu proceso de transformación digital con nuestro enfoque metodológico probado
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                type="button"
                onClick={() => {
                  const phone = '51947726382';
                  const message = `Hola. Me interesa solicitar una consultoría gratuita sobre software empresarial con ${BRAND_NAME}.`;
                  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-200 active:scale-95 shadow-corporate font-display text-readable-base touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                <TechIcon name="chat" className="w-5 h-5" />
                Solicitar Consultoría Gratuita
              </button>
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/servicios';
                }}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-200 active:scale-95 shadow-corporate font-display text-readable-base touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                <TechIcon name="services" className="w-5 h-5" />
                Ver Nuestros Servicios
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessProcess;
